/**
 * Settings, as a sheet off the command bar.
 *
 * There is no settings *screen* because there is no second screen in this product — the
 * graph is the app, and leaving it to change a lens would mean leaving the thing the lens
 * is about. Everything here takes effect on the next branch request, so the sheet can be
 * opened, changed and dismissed between two chords.
 *
 * ## Why the learned preferences are shown raw
 *
 * `state/preferences.ts` sets out the anti-trap requirement in full: personalisation is
 * allowed to lean, never to narrow, and one of the three things enforcing that is *this
 * table*. A player can see exactly what the app thinks of them — the same numbers that
 * ride along with every branch request — and undo the whole lot with one button. A
 * personalisation model nobody can inspect is one nobody can disagree with.
 *
 * The reset goes through `view.resetPreferences` when the host offers one, because the
 * running `PreferencesController` keeps the tallies in memory and would write them back on
 * the next chord. Clearing storage directly is the fallback for when nothing is running.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  MOVEMENT_KINDS,
  PERSPECTIVES,
  noteName,
  spellPitchClass,
  type ModelChoice,
  type Perspective,
  type PitchClass,
  type PreferenceHints,
} from '@hc/shared';
import {
  storageStatus,
  subscribeToStorageStatus,
  type StorageStatus,
  type StorageUnavailableReason,
} from '../../sketchbook/db.js';
import { loadPreferenceHints, resetPreferences } from '../../sketchbook/queries.js';
import { adventurousnessLabel } from '../../commands/localCommands.js';
import {
  isSpeechOutputSupported,
  loadSpokenResponsesEnabled,
  saveSpokenResponsesEnabled,
} from '../../commands/voice.js';
import { BottomSheet } from '../system/BottomSheet.js';
import { useSessionView, type SessionView } from '../system/sessionView.js';
import '../input/commandbar.css';

/** How far one drag of the slider moves. Fine enough to feel, coarse enough to land on. */
export const ADVENTUROUSNESS_STEP = 0.05;

/**
 * The three tiers, in the order they are offered: deepest first, because that is the order
 * of the one trade-off worth stating.
 *
 * Names, not provider model IDs. The server owns the mapping (`llm/models.ts`), so a
 * provider rename is not a change to this file — and "gpt-something-mini" in front of
 * somebody deciding whether to wait two seconds is information they cannot use.
 */
export const MODEL_TIERS: ReadonlyArray<{ value: ModelChoice; label: string; note: string }> =
  Object.freeze([
    { value: 'sol', label: 'Sol', note: 'deepest' },
    { value: 'terra', label: 'Terra', note: 'balanced' },
    { value: 'luna', label: 'Luna', note: 'fastest' },
  ]);

export interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Overrides the view from context, exactly as `SessionPage` does. */
  view?: SessionView;
  /** Injected in tests, so the drawer never has to open a database. */
  loadHints?: () => Promise<PreferenceHints>;
  /** Overrides both the host's reset and the storage-only fallback. Tests only. */
  resetHints?: () => Promise<PreferenceHints>;
  /** Injected in tests. Defaults to what the platform can actually say out loud. */
  speechOutputSupported?: boolean;
}

// ---------------------------------------------------------------------------
// The two things worth stating in prose
// ---------------------------------------------------------------------------

const STORAGE_REASONS: Readonly<Record<StorageUnavailableReason, string>> = Object.freeze({
  none: '',
  'no-indexeddb': 'this browser exposes no on-device database',
  'open-failed': 'the database would not open',
  'open-timed-out': 'the database never answered',
  'write-failed': 'a write was refused',
});

/**
 * One honest line about durability.
 *
 * Private browsing is the tested path, not an edge case, and "nothing is being saved" is
 * something a player must be able to find out *before* they lose an hour of doodling.
 */
export function storageDurabilityNote(status: StorageStatus): string {
  if (status.durable) return 'Sketches and preferences are kept on this device.';
  const reason = STORAGE_REASONS[status.reason];
  return `Nothing is being saved${reason ? ` — ${reason}` : ''}. This session works, but a refresh will lose it.`;
}

export interface HintRow {
  key: string;
  label: string;
  value: string;
}

/** A pitch class named from its own point of view: 6 is F♯ in F♯, not G♭. */
function tonicName(pc: PitchClass): string {
  return noteName(spellPitchClass(pc, { tonicPc: pc, mode: 'major' }));
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * The hints as a table. Exported separately from the component because the point of the
 * table is *what it says*, and that is worth asserting without a DOM.
 */
export function describeHints(hints: PreferenceHints): HintRow[] {
  const rows: HintRow[] = [];

  const taken = MOVEMENT_KINDS.map((kind) => ({ kind, count: hints.takenKindCounts[kind] ?? 0 }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count);
  for (const { kind, count } of taken) {
    rows.push({ key: `taken:${kind}`, label: kind.replace(/-/g, ' '), value: `${count} taken` });
  }

  rows.push({
    key: 'adventurousness',
    label: 'settles around',
    value: `${adventurousnessLabel(hints.adventurousnessEma)} (${percent(hints.adventurousnessEma)})`,
  });
  rows.push({
    key: 'explanations',
    label: 'opens explanations',
    value: percent(hints.explanationTapRate),
  });
  if (hints.commonKeys.length > 0) {
    rows.push({
      key: 'keys',
      label: 'plays in',
      value: hints.commonKeys.map(tonicName).join(' · '),
    });
  }
  if (hints.likedTransitions.length > 0) {
    rows.push({
      key: 'liked',
      label: 'marked as liked',
      value: `${hints.likedTransitions.length} transitions`,
    });
  }

  return rows;
}

// ---------------------------------------------------------------------------

export function SettingsDrawer({
  open,
  onClose,
  view: injected,
  loadHints = loadPreferenceHints,
  resetHints,
  speechOutputSupported = isSpeechOutputSupported(),
}: SettingsDrawerProps): React.JSX.Element {
  const view = useSessionView(injected);
  const { state, dispatch, settings, updateSettings } = view;
  const forget = resetHints ?? view.resetPreferences ?? resetPreferences;

  const [hints, setHints] = useState<PreferenceHints | null>(null);
  const [storage, setStorage] = useState<StorageStatus>(() => storageStatus());

  // Read on open rather than on mount: the hints are a page of numbers nobody is looking
  // at until they ask, and the read touches IndexedDB.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void loadHints()
      .then((loaded) => {
        if (!cancelled) setHints(loaded);
      })
      // Storage that will not answer is already reported by the durability line below.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, loadHints]);

  useEffect(() => subscribeToStorageStatus(setStorage), []);

  // The host holds settings in React state, so without this the spoken-replies choice
  // would be forgotten on every reload. Runs once, and only ever pushes the stored `true`
  // up: false is already the default, and within a session the host is the authority.
  useEffect(() => {
    if (loadSpokenResponsesEnabled()) updateSettings?.({ speakReplies: true });
  }, [updateSettings]);

  const togglePerspective = useCallback(
    (perspective: Perspective) => {
      const active = new Set(state.perspectives);
      if (!active.delete(perspective)) active.add(perspective);
      // Filtered through the canonical order so turning one off and on again does not
      // quietly reorder the prompt the model sees.
      dispatch({
        type: 'SET_PERSPECTIVES',
        perspectives: PERSPECTIVES.filter((p) => active.has(p)),
      });
    },
    [dispatch, state.perspectives],
  );

  const setSpeakReplies = useCallback(
    (value: boolean) => {
      updateSettings?.({ speakReplies: value });
      // The host keeps settings in React state only, so this is what makes the choice
      // survive a reload. `voice.ts` owns the key and the false-by-default rule.
      saveSpokenResponsesEnabled(value);
    },
    [updateSettings],
  );

  const reset = useCallback(() => {
    void forget()
      .then(setHints)
      .catch(() => {});
  }, [forget]);

  const rows = hints ? describeHints(hints) : [];
  const speakReplies = settings?.speakReplies ?? false;
  const prefetch = settings?.prefetch ?? true;
  const collectRecognitions = settings?.collectRecognitions ?? false;
  const soundOnTheGo = settings?.soundOnTheGo ?? true;
  const aiCompanion = settings?.aiCompanion ?? false;
  const modelChoice = settings?.modelChoice ?? 'luna';
  const showPrompts = settings?.showPrompts ?? false;
  const onTheGo = view.mode === 'on-the-go';

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Settings"
      eyebrow="how this behaves"
      testId="hc-settings-sheet"
    >
      <section className="hc-settings__group">
        <label className="hc-settings__toggle">
          <input
            type="checkbox"
            data-testid="hc-setting-speak"
            checked={speakReplies && speechOutputSupported}
            disabled={!speechOutputSupported}
            onChange={(event) => setSpeakReplies(event.target.checked)}
          />
          <span className="hc-settings__toggle-text">
            <span>Speak short answers</span>
            <span className="hc-settings__hint">
              {speechOutputSupported
                ? 'Only direct answers, never suggestions.'
                : 'This browser has no speech synthesis.'}
            </span>
          </span>
        </label>

        {/*
          Look-ahead is latency work, and with the built-in engine answering there is no
          latency to spend it on — the set is there in the same tick. So the control goes
          where the tier picker goes: hidden, rather than sitting there inert.
        */}
        {aiCompanion ? (
          <label className="hc-settings__toggle">
            <input
              type="checkbox"
              data-testid="hc-setting-prefetch"
              checked={prefetch}
              onChange={(event) => updateSettings?.({ prefetch: event.target.checked })}
            />
            <span className="hc-settings__toggle-text">
              <span>Look ahead</span>
              <span className="hc-settings__hint">
                Fetches branches for the likely next chords, so they are already there.
              </span>
            </span>
          </label>
        ) : null}

        {/*
          Sound is not offered at the piano, and not merely because it would be redundant:
          the microphone is open there and would hear the speaker, feeding the recogniser
          its own output. So the control exists exactly where it can be used.
        */}
        {onTheGo ? (
          <label className="hc-settings__toggle">
            <input
              type="checkbox"
              data-testid="hc-setting-sound"
              checked={soundOnTheGo}
              onChange={(event) => updateSettings?.({ soundOnTheGo: event.target.checked })}
            />
            <span className="hc-settings__toggle-text">
              <span>Sound</span>
              <span className="hc-settings__hint">Hear chords as you tap them.</span>
            </span>
          </label>
        ) : null}

        <label className="hc-settings__toggle">
          <input
            type="checkbox"
            data-testid="hc-setting-collect"
            checked={collectRecognitions}
            onChange={(event) => updateSettings?.({ collectRecognitions: event.target.checked })}
          />
          <span className="hc-settings__toggle-text">
            <span>Collect recognition feedback</span>
            {/*
              One honest line. It says what is kept and it says that ignoring the offer is
              itself recorded, because a player who thought silence meant nothing would be
              generating data they did not agree to.
            */}
            <span className="hc-settings__hint">
              Shows ✓/✗ beside each chord it hears and keeps your answer — including no answer — on
              this device, to tune recognition later.
            </span>
          </span>
        </label>
      </section>

      {/*
        The one switch that decides what the rest of this sheet is for.

        Above the tier picker because it governs it: with the companion off there is no
        tier, no prompt to show and no lens to bias, so those controls are not dimmed —
        they are absent. Dimming would say "not now"; absence says "not part of this app
        as it is currently set up", which is the truth.
      */}
      <section className="hc-settings__group">
        <label className="hc-settings__toggle">
          <input
            type="checkbox"
            data-testid="hc-setting-ai"
            checked={aiCompanion}
            onChange={(event) => updateSettings?.({ aiCompanion: event.target.checked })}
          />
          <span className="hc-settings__toggle-text">
            <span>AI companion</span>
            <span className="hc-settings__hint">
              On, suggestions and their explanations are written by a model over the network. Off,
              they come from the built-in engine — instant, offline, and the same graph.
            </span>
          </span>
        </label>

        {aiCompanion ? (
          <>
            <h3 className="hc-settings__heading">Model</h3>
            {/*
              One honest line, and only one. The tiers are named rather than numbered
              because a provider model ID is not a thing to put in front of a player — and
              because the mapping is the server's, so a rename must not reach this file.
            */}
            <p className="hc-settings__hint">Deeper thinking takes longer to answer.</p>
            <div className="hc-settings__segmented" role="radiogroup" aria-label="Model">
              {MODEL_TIERS.map((tier) => {
                const on = modelChoice === tier.value;
                return (
                  <button
                    key={tier.value}
                    type="button"
                    className={`hc-settings__segment${on ? ' is-on' : ''}`}
                    data-testid="hc-model-choice"
                    data-model={tier.value}
                    role="radio"
                    aria-checked={on}
                    onClick={() => updateSettings?.({ modelChoice: tier.value })}
                  >
                    <span className="hc-settings__segment-name">{tier.label}</span>
                    <span className="hc-settings__segment-note">{tier.note}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </section>

      {/*
        Lenses are prompt material — a style instruction for something that reads. The
        built-in engine reads nothing, so with the companion off these chips would set a
        field that reaches no one.
      */}
      {aiCompanion ? (
        <section className="hc-settings__group">
          <h3 className="hc-settings__heading">Lenses</h3>
          <p className="hc-settings__hint">
            Several at once is normal — jazz and modal is a different graph from either.
          </p>
          <div className="hc-settings__chips">
            {PERSPECTIVES.map((perspective) => {
              const on = state.perspectives.includes(perspective);
              return (
                <button
                  key={perspective}
                  type="button"
                  className={`hc-settings__chip${on ? ' is-on' : ''}`}
                  data-testid="hc-perspective"
                  data-perspective={perspective}
                  aria-pressed={on}
                  onClick={() => togglePerspective(perspective)}
                >
                  {perspective}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="hc-settings__group">
        <h3 className="hc-settings__heading">
          Adventurousness
          <span className="hc-settings__value" data-testid="hc-adventurousness-label">
            {adventurousnessLabel(state.adventurousness)}
          </span>
        </h3>
        <input
          className="hc-settings__slider"
          data-testid="hc-adventurousness"
          type="range"
          min={0}
          max={1}
          step={ADVENTUROUSNESS_STEP}
          value={state.adventurousness}
          aria-label="Adventurousness"
          onChange={(event) =>
            dispatch({ type: 'SET_ADVENTUROUSNESS', value: Number(event.target.value) })
          }
        />
      </section>

      <section className="hc-settings__group">
        <h3 className="hc-settings__heading">What this app has learned</h3>
        <p className="hc-settings__hint">
          Exactly what is sent with a request for suggestions. It leans; it never filters.
        </p>
        {rows.length > 0 ? (
          <table className="hc-settings__table" data-testid="hc-hints-table">
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} data-testid="hc-hint-row" data-hint={row.key}>
                  <th scope="row">{row.label}</th>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="hc-settings__hint" data-testid="hc-hints-empty">
            Nothing learned yet.
          </p>
        )}
        <button
          type="button"
          className="hc-button hc-button--quiet"
          data-testid="hc-hints-reset"
          onClick={reset}
        >
          Forget what you have learned
        </button>
      </section>

      {/* Nothing would ever be logged into it: with the companion off no prompt is sent. */}
      {aiCompanion ? (
        <section className="hc-settings__group">
          <label className="hc-settings__toggle">
            <input
              type="checkbox"
              data-testid="hc-setting-prompts"
              checked={showPrompts}
              onChange={(event) => updateSettings?.({ showPrompts: event.target.checked })}
            />
            <span className="hc-settings__toggle-text">
              <span>Show model prompts</span>
              <span className="hc-settings__hint">
                See exactly what is sent to OpenAI, and whether an answer came from the cache. A
                “prompts” button appears beside the gear.
              </span>
            </span>
          </label>
        </section>
      ) : null}

      <p className="hc-settings__storage" data-testid="hc-storage-note">
        {storageDurabilityNote(storage)}
      </p>
    </BottomSheet>
  );
}

export default SettingsDrawer;
